import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchApiGet,
  fetchApiPost,
  fetchApiPostBlob,
  MimeType,
} from "../utils";
import { BaseDbObject } from "@/lib/models/db";
import { DocumentTemplate, DocumentTemplateInstance } from "../signingContracts";
import { zodStrictRequiredDecimal } from "../common";
import { Customer } from "../reports";

export enum AcceptedDestinationPacketFields {
  // System fields
  CUSTOMER_NAME = "customer_name",
  CUSTOMER_EMAIL = "customer_email",

  LENDER_NAME = "lender_name",
  LENDER_INSTITUTION = "lender_institution",
  LENDER_EMAIL = "lender_email",
}

export enum DocumentVariant {
  DESTINATION_INFO = "destination_info",
  CREDIT_APPLICATION = "credit_application",
}

export interface DestinationPacketDocument extends BaseDbObject {
  destination_template_packet_id: string | null;
  document_template_id: string;
  document_variant: DocumentVariant;
  DocumentTemplate?: DocumentTemplate;
  DestinationTemplatePacket?: DestinationTemplatePacket | null;
}

export interface DestinationTemplatePacket extends BaseDbObject {
  name: string | null;
  deleted_at: Date | null;
}

export interface DestinationPacketEnvelope extends BaseDbObject {
  destination_template_packet_id: string;
  customer_id: string;
  lender_name: string | null;
  lender_institution: string | null;
  lender_email: string | null;
  DestinationTemplatePacket?: DestinationTemplatePacket | null;
  Customer?: Customer | null;
}

export interface DestinationPacketInstance extends BaseDbObject {
  destination_packet_envelop_id: string;
  destination_packet_document_id: string;
  document_template_instance_id: string;
  DestinationPacketEnvelop?: DestinationPacketEnvelope | null;
  DestinationPacketDocument?: DestinationPacketDocument | null;
  DocumentTemplateInstance?: DocumentTemplateInstance | null;
}

export interface ListDestinationPacketDocumentsResponse {
  data: DestinationPacketDocument[];
}

export interface ListDestinationPacketInstancesResponse {
  data: DestinationPacketInstance[];
}

export interface CreateDestinationTemplateDocumentResponse {
  destination_template_packet_id: string;
  destination_packet_document_id: string;
  document_template_id: string;
  upload_url: string;
}

export interface GetTemplateDocumentResponse {
  document_url: string;
}

export interface CreateEnvelopeInstanceResponse {
  envelope_id: string;
  signing_session_ids: string[];
}

export interface DestinationPacketInstanceDocumentResponse {
  sent_document_url: string | null;
  final_document_url: string | null;
}

const accepted_fields = Object.values(AcceptedDestinationPacketFields);

export const acceptedFieldsOptions = [
  { value: AcceptedDestinationPacketFields.CUSTOMER_NAME, label: "Customer Name" },
  { value: AcceptedDestinationPacketFields.CUSTOMER_EMAIL, label: "Customer Email" },
  { value: AcceptedDestinationPacketFields.LENDER_NAME, label: "Lender Name" },
  { value: AcceptedDestinationPacketFields.LENDER_INSTITUTION, label: "Lender Institution" },
  { value: AcceptedDestinationPacketFields.LENDER_EMAIL, label: "Lender Email" },
];

export enum SignerType {
  PRIMARY_CUSTOMER = "1",
  SECONDARY_CUSTOMER = "2",
  LENDER = "3",
}

export const signerOptions = [
  { value: SignerType.PRIMARY_CUSTOMER, label: "Primary Customer" },
  { value: SignerType.SECONDARY_CUSTOMER, label: "Secondary Customer" },
  { value: SignerType.LENDER, label: "Lender" },
];

const DestinationPacketPositionData = z.object({
  x: zodStrictRequiredDecimal({ key: "x" }),
  y: zodStrictRequiredDecimal({ key: "y" }),
  width: zodStrictRequiredDecimal({ key: "width" }),
  height: zodStrictRequiredDecimal({ key: "height" }),
  page: z.number().min(1),
  text_size: z.number().min(6).max(20),
});

const DestinationPacketField = z.object({
  field_type: z.enum(["system", "signature"]),
  field_id: z.string().min(1),
  field_key: z.string().min(1),
  field_dest_type: z.enum([
    "signature",
    "signature_initials",
    "date_signed",
    "checkbox",
    "text",
    "attachment",
    "note",
  ]).optional(),
  position_data: DestinationPacketPositionData,
  signer_id: z.string().optional(),
  is_required: z.boolean(),
  is_readonly: z.boolean(),
  freeform_value: z.string().optional(),
}).refine((data) => {
  if (data.field_type === "signature" && !data.field_dest_type) {
    return false;
  }
  return true;
}, {
  message: "Signature fields must have field_dest_type",
  path: ["field_dest_type"],
});

const DestinationPacketTemplateValidation = z.object({
  version_label: z.string().nullable().optional(),
  version_reason: z.string().nullable().optional(),
  require_all_signers: z.boolean(),
  send_auto_reminders: z.boolean(),
  use_signer_order: z.boolean(),
  name: z.string().optional(),
  fields: z.array(DestinationPacketField),
});

export const CreateDestinationTemplateValidation = z.object({
  template_content_type: z.literal("destination_packet"),
  parent_group_id: z.string().uuid().optional(),
  destination_template_packet_id: z.string().uuid().optional(),
  packet_name: z.string().optional(),
  document_variant: z.enum([DocumentVariant.DESTINATION_INFO, DocumentVariant.CREDIT_APPLICATION]),
  template: DestinationPacketTemplateValidation,
}).refine((data) => {
  const fields = data.template.fields || [];
  const field_ids: string[] = fields.map((field) => field.field_id);

  const uniqueFieldIds = new Set(field_ids);
  const isValidFieldIds = field_ids.every((id: string) => {
    // For system fields, check against accepted fields
    const field = fields.find((f) => f.field_id === id);
    if (field?.field_type === "system") {
      return [AcceptedDestinationPacketFields.CUSTOMER_NAME, AcceptedDestinationPacketFields.CUSTOMER_EMAIL].includes(id as AcceptedDestinationPacketFields);
    }

    return true;
  });

  return field_ids.length === uniqueFieldIds.size && isValidFieldIds;
}, {
  message: "May not use unknown system fields",
  path: ["template", "fields"],
}).refine((data) => {
  // Ensure required tracked fields are present exactly once
  const fields = data.template.fields || [];
  const field_ids: string[] = fields.map((field) => field.field_id);

  const requiredFields = [
    AcceptedDestinationPacketFields.CUSTOMER_NAME,
    AcceptedDestinationPacketFields.CUSTOMER_EMAIL,
  ];

  // Only require lender fields for credit application variant
  if (data.document_variant === DocumentVariant.CREDIT_APPLICATION) {
    requiredFields.push(
      AcceptedDestinationPacketFields.LENDER_NAME,
      AcceptedDestinationPacketFields.LENDER_EMAIL
    );
  }

  const missingFields = requiredFields.filter(reqId => !field_ids.includes(reqId));
  return missingFields.length === 0;
}, {
  message: "Template must include all required fields for the selected document variant",
  path: ["template", "fields"],
}).refine((data) => {
  // Ensure required tracked fields appear exactly once (no duplicates)
  const fields = data.template.fields || [];
  const field_ids: string[] = fields.map((field) => field.field_id);

  const requiredFields = [
    AcceptedDestinationPacketFields.CUSTOMER_NAME,
    AcceptedDestinationPacketFields.CUSTOMER_EMAIL,
  ];

  // Only require lender fields for credit application variant
  if (data.document_variant === DocumentVariant.CREDIT_APPLICATION) {
    requiredFields.push(
      AcceptedDestinationPacketFields.LENDER_NAME,
      AcceptedDestinationPacketFields.LENDER_EMAIL
    );
  }

  const counts = requiredFields.map(reqId => field_ids.filter(id => id === reqId).length);
  return counts.every(count => count === 1);
}, {
  message: "Required fields must appear exactly once",
  path: ["template", "fields"],
});

export const PreviewTemplateValidation = z.object({
  customer_name: z.string().optional(),
  customer_email: z.string().email().optional(),
});

export const CreateEnvelopeInstanceValidation = z.object({
  customer_id: z.string().uuid(),
  destination_packet_document_id: z.string().uuid(),
  documents: z.array(
    z.object({
      id: z.string().uuid(),
      override_fields: z.object({
        customer_name: z.string().optional(),
        customer_email: z.string().email().optional(),
      }).optional(),
    })
  ).min(1),
  is_test_send: z.boolean().default(false),
  is_draft: z.boolean().default(false),
});

export function useListDestinationPacketTemplates() {
  return useQuery({
    queryKey: ["destinationPackets", "templates"],
    queryFn: () => fetchApiGet<ListDestinationPacketDocumentsResponse>(
      `contracts/destination_packets/template`
    ),
    retry: 1,
  });
}

export function useGetDestinationPacketTemplate({ id, enabled }: { id: string; enabled?: boolean }) {
  return useQuery({
    queryKey: ["destinationPackets", "template", id],
    queryFn: () => fetchApiGet<DestinationPacketDocument>(`contracts/destination_packets/template/${id}`),
    retry: 1,
    enabled,
  });
}

export function useCreateDestinationTemplate() {
  const queryClient = useQueryClient();

  return async (params: z.infer<typeof CreateDestinationTemplateValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<CreateDestinationTemplateDocumentResponse>(
      `contracts/destination_packets/template`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["destinationPackets"] });
    }

    return result;
  };
}

export function useGetDestinationTemplateDocument() {
  const queryClient = useQueryClient();

  return async (template_id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<GetTemplateDocumentResponse>(
      `contracts/destination_packets/template/${template_id}/file`,
      {},
      queryParams
    );

    return result;
  };
}

export function useGetDestinationPreviewTemplateDocument() {
  const queryClient = useQueryClient();

  return async (params: {
    template_id: string;
    customer_name?: string;
    customer_email?: string;
  }) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPostBlob(
      `contracts/destination_packets/template/${params.template_id}/preview`,
      {
        customer_name: params.customer_name,
        customer_email: params.customer_email,
      },
      MimeType.Json,
      queryParams
    );

    return result;
  };
}

export function useCreateDestinationEnvelopeInstance() {
  const queryClient = useQueryClient();

  return async (params: z.infer<typeof CreateEnvelopeInstanceValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<CreateEnvelopeInstanceResponse>(
      `contracts/destination_packets/envelope/instance`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["destinationPackets"] });
    }

    return result;
  };
}

export interface DestinationPacketInstanceQueryParams {
  template_id?: string;
  envelope_id?: string;
  customer_id?: string;
}

function buildDestinationPacketInstanceQuery(filters?: DestinationPacketInstanceQueryParams) {
  const queryParams = new URLSearchParams();
  if (filters?.template_id) {
    queryParams.set("template_id", filters.template_id);
  }
  if (filters?.envelope_id) {
    queryParams.set("envelope_id", filters.envelope_id);
  }
  if (filters?.customer_id) {
    queryParams.set("customer_id", filters.customer_id);
  }

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : "";
}

export function useListDestinationPacketInstances(
  filters?: DestinationPacketInstanceQueryParams,
  options?: { enabled?: boolean },
) {
  const filterKey = JSON.stringify(filters ?? {});

  return useQuery({
    queryKey: ["destinationPackets", "instances", filterKey],
    queryFn: () =>
      fetchApiGet<ListDestinationPacketInstancesResponse>(
        `contracts/destination_packets/instance${buildDestinationPacketInstanceQuery(filters)}`,
      ),
    retry: 1,
    enabled: options?.enabled ?? true,
  });
}

export function useGetDestinationPacketInstance({
  template_id,
  instance_id,
  enabled,
}: {
  template_id: string;
  instance_id: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["destinationPackets", "template", template_id, "instance", instance_id],
    queryFn: () =>
      fetchApiGet<DestinationPacketInstance>(
        `contracts/destination_packets/template/${template_id}/instance/${instance_id}`,
      ),
    retry: 1,
    enabled: Boolean(template_id && instance_id) && (enabled ?? true),
  });
}

export function useGetDestinationPacketInstanceDocument() {
  const queryClient = useQueryClient();

  return async (template_id: string, instance_id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<DestinationPacketInstanceDocumentResponse>(
      `contracts/destination_packets/template/${template_id}/instance/${instance_id}/file`,
      {},
      queryParams,
    );

    return result;
  };
}

